import { FC, useEffect, useState } from "react";
import { Button, Dialog, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { CASE_FIELD_AUTHOR, CaseMedicalField, MEDICAL_FIELD_CHOICE, PendingFieldChoiceModel } from "@shared/models";
import { showNotification } from "@shared/store/actions";
import { selectors } from "@containers/Cases/store";
import { chooseMedicalFieldValue } from "@containers/Cases/store/actions";
import {
  MEDICAL_FIELD_LABELS,
  MEDICAL_FIELDS_FORM_ORDER,
  MITIGATING_FACTORS_SECTION_ID,
} from "@containers/Cases/constants";
import { pluralize } from "@containers/Cases/utils/caseDocuments";

import { CaseDocumentModalHeader } from "../CaseDocumentModal";
import ChooseFieldValueOption, { FieldValueOption } from "./ChooseFieldValueOption";

const NO_CHOICES: PendingFieldChoiceModel[] = [];

// Wording proposed in the PRD's open question 12.
const CHOICES_SAVED_MESSAGE = "Values saved.";

const getFieldOrder = (choices: PendingFieldChoiceModel[], firstField?: CaseMedicalField) => {
  const fields = MEDICAL_FIELDS_FORM_ORDER.filter((field) => choices.some((choice) => choice.field === field));

  return firstField && fields.includes(firstField)
    ? [firstField, ...fields.filter((field) => field !== firstField)]
    : fields;
};

const getOptions = ({
  current_text,
  current_author,
  current_source,
  new_text,
  new_files,
  flag_reason,
}: PendingFieldChoiceModel): FieldValueOption[] => {
  const isPsrText = current_author === CASE_FIELD_AUTHOR.PSR;

  return [
    {
      choice: MEDICAL_FIELD_CHOICE.CURRENT,
      title: isPsrText ? "Keep PSR value" : "Keep current value",
      source: isPsrText || !current_source ? current_source : `Entered by ${current_source}`,
      text: current_text,
    },
    {
      choice: MEDICAL_FIELD_CHOICE.NEW,
      title: "Use medical summaries value",
      source: pluralize(new_files, "file"),
      text: new_text,
      flagReason: flag_reason,
    },
    {
      choice: MEDICAL_FIELD_CHOICE.BOTH,
      title: "Keep both",
      source: isPsrText ? "PSR first, then summaries" : "Current value first, then summaries",
      text: "Both saved in one field, separated by a blank line.",
      flagReason: flag_reason,
    },
  ];
};

const scrollToMitigatingFactors = () => {
  // After the window has closed and given the focus back, which can scroll the page.
  window.setTimeout(() => {
    document.getElementById(MITIGATING_FACTORS_SECTION_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 100);
};

interface ChooseFieldValuesModalProps {
  caseId: number;
  /** The field to start at; the first pending one when left out */
  field?: CaseMedicalField;
  onClose: () => void;
}

const ChooseFieldValuesModal: FC<ChooseFieldValuesModalProps> = ({ caseId, field, onClose }) => {
  const dispatch = useDispatch();
  const pendingChoices = useSelector(selectors.getCase())?.pending_choices ?? NO_CHOICES;
  // Fixed when the window opens, so that "{i} OF {n}" counts the saved choices too.
  const [fields] = useState(() => getFieldOrder(pendingChoices, field));
  const [choice, setChoice] = useState(MEDICAL_FIELD_CHOICE.NEW);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remainingFields = fields.filter((item) => pendingChoices.some((pending) => pending.field === item));
  const pendingChoice = pendingChoices.find((pending) => pending.field === remainingFields[0]);
  const isLast = remainingFields.length === 1;

  useEffect(() => {
    if (!pendingChoice) onClose();
  }, [pendingChoice, onClose]);

  if (!pendingChoice) return null;

  const handleSave = () => {
    setIsSaving(true);
    setError(null);
    dispatch(
      chooseMedicalFieldValue({
        caseId,
        field: pendingChoice.field,
        choice,
        cb: () => {
          setIsSaving(false);
          setChoice(MEDICAL_FIELD_CHOICE.NEW);

          if (isLast) {
            onClose();
            scrollToMitigatingFactors();
            dispatch(showNotification({ message: CHOICES_SAVED_MESSAGE, appearance: "success" }));
          }
        },
        onError: (message) => {
          setIsSaving(false);
          setError(message);
        },
      }),
    );
  };

  return (
    <>
      <CaseDocumentModalHeader
        overline={`CHOOSE A VALUE · ${fields.length - remainingFields.length + 1} OF ${fields.length}`}
        title={MEDICAL_FIELD_LABELS[pendingChoice.field]}
        subtitle="The PSR and the medical summaries give different details. Pick what to save."
      />
      <Dialog.Body display="flex" flexDirection="column" gap={3}>
        <RadioGroup.Root
          value={choice}
          disabled={isSaving}
          onValueChange={({ value }) => value && setChoice(value as MEDICAL_FIELD_CHOICE)}
        >
          <Stack gap="10px">
            {getOptions(pendingChoice).map((option) => (
              <ChooseFieldValueOption key={option.choice} option={option} selected={option.choice === choice} />
            ))}
          </Stack>
        </RadioGroup.Root>
        {error && (
          <Text fontSize="13px" color="brand.error">
            {error}
          </Text>
        )}
      </Dialog.Body>
      <Dialog.Footer justifyContent="space-between" gap={3} mt={2} borderTop="1px solid" borderColor="gray.200">
        <Button variant="neutral" fontSize="14px" disabled={isSaving} onClick={onClose}>
          Decide later
        </Button>
        <Button variant="primary" fontSize="14px" loading={isSaving} onClick={handleSave}>
          {isLast ? "Save choice" : "Save & next"}
        </Button>
      </Dialog.Footer>
    </>
  );
};

export default ChooseFieldValuesModal;
